<template>
  <v-select
    v-bind="attrs"
    v-model="model"
    :items="selectItems"
    item-title="title"
    item-value="value">
    <template v-slot:item="slotProps">
      <v-list-item
        v-bind="slotProps.props"
        :active="false"
        :border="slotProps.index > 0 ? 't' : false"
        :title="row(slotProps).title"
        :subtitle="row(slotProps).seatsLine ?? undefined">
        <template v-slot:append>
          <div class="d-flex align-center flex-nowrap license-select-append">
            <div
              v-if="row(slotProps).expiryPrimary"
              class="text-end ps-2 license-select-expiry"
              style="min-width: 7.5rem">
              <div
                class="text-body-2 font-weight-medium"
                :class="row(slotProps).expirySoon ? 'text-warning' : 'text-medium-emphasis'">
                {{ row(slotProps).expiryPrimary }}
              </div>
              <div v-if="row(slotProps).expiryHint" class="text-caption text-medium-emphasis">
                {{ row(slotProps).expiryHint }}
              </div>
            </div>
            <v-checkbox
              :model-value="isMenuCheckboxChecked(slotProps)"
              density="compact"
              hide-details
              readonly
              class="license-select-row-check" />
          </div>
        </template>
      </v-list-item>
    </template>
    <template v-slot:selection="slotProps">
      <div class="license-select-selection text-start">
        <span class="v-select__selection-text text-body-2 d-block">{{ row(slotProps).title }}</span>
        <span class="text-caption text-medium-emphasis d-block license-select-selection__sub">
          {{ row(slotProps).selectionExpiryLine }}
        </span>
      </div>
    </template>
  </v-select>
</template>

<script lang="ts" setup>
import { computed, useAttrs } from "vue";
import { useI18n } from "vue-i18n";
import type { License } from "@/types/license";
import { useLicense } from "@/lib/license";

defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    licenses?: License[];
    /** Prepends the free tier row (`modelValue` `null`). */
    withFreeTier?: boolean;
    /**
     * Optional first row (e.g. bulk toolbar: “Change license type” with sentinel `value` like `-1`).
     * Renders before the free tier when `withFreeTier` is on.
     */
    actionHeadline?: { title: string; value: number };
    /**
     * When `v-model` is a toolbar sentinel (e.g. `-1`) but every selected user shares one license,
     * pass that id (or `null` for free tier) so the matching menu row still shows a checked checkbox.
     */
    menuCheckedLicenseValue?: number | null;
  }>(),
  {
    licenses: () => [],
    withFreeTier: true
  }
);

const model = defineModel<number | null | number[]>({ required: true });

const attrs = useAttrs();

const { t, d } = useI18n({
  inheritLocale: true,
  useScope: "global"
});
const { getLicenseTypeName } = useLicense();

const DAY_MS = 86400000;
/** Same rule as the design: under one month → show days left. */
const SOON_MS = 30 * DAY_MS;
const SEATS_CAP_THRESHOLD = 999;

interface LicenseSelectRow {
  title: string;
  /** License id, `null` (free tier), or a caller sentinel such as `-1` for a headline row. */
  value: number | null;
  seatsLine: string | null;
  expiryPrimary: string | null;
  expiryHint: string | null;
  expirySoon: boolean;
  /** Second line when the menu is closed (expiry only, not seats). */
  selectionExpiryLine: string | null;
  props: { disabled: boolean };
}

type ListItemInternal = { raw?: unknown };
type ItemSlotProps = { item?: ListItemInternal };

function row(slotProps: ItemSlotProps): LicenseSelectRow {
  const raw = slotProps.item?.raw;
  if(raw && typeof raw === "object" && "value" in raw) {
    return raw as LicenseSelectRow;
  }
  return {
    title: "",
    value: null,
    seatsLine: null,
    expiryPrimary: null,
    expiryHint: null,
    expirySoon: false,
    selectionExpiryLine: null,
    props: { disabled: false }
  };
}

function isExpired(license: License, now: Date): boolean {
  if(license.expireAt == null) {
    return false;
  }
  return new Date(license.expireAt).getTime() <= now.getTime();
}

function modelReferencesLicenseId(modelValue: number | null | number[], licenseId: number): boolean {
  if(Array.isArray(modelValue)) {
    return modelValue.includes(licenseId);
  }
  if(typeof modelValue === "number") {
    return modelValue === licenseId;
  }
  return false;
}

/** Checkbox mirrors v-model; interaction stays on the list row (readonly). */
function isRowSelected(slotProps: ItemSlotProps): boolean {
  const v = row(slotProps).value;
  const m = model.value;
  if(Array.isArray(m)) {
    return m.some((x) => x === v);
  }
  return m === v;
}

function isMenuCheckboxChecked(slotProps: ItemSlotProps): boolean {
  if(isRowSelected(slotProps)) {
    return true;
  }
  const hint = props.menuCheckedLicenseValue;
  if(hint === undefined) {
    return false;
  }
  return row(slotProps).value === hint;
}

function formatSeatsLine(freeSeats: number): string {
  if(freeSeats >= SEATS_CAP_THRESHOLD) {
    return t("license_select_seats_cap");
  }
  if(freeSeats === 1) {
    return t("license_select_seat_one");
  }
  return t("license_select_seats_n", { count: freeSeats });
}

function licenseToRow(license: License, now: Date, listDisabled: boolean): LicenseSelectRow {
  const usedSeats = license._count?.users ?? 0;
  const freeSeats = Math.max(0, license.maxUsers - usedSeats);
  const seatsLine = formatSeatsLine(freeSeats);
  const expiryHint = t("license_select_expires_caption");

  if(license.expireAt == null) {
    const expiryPrimary = t("license_select_perpetual");
    return {
      title: getLicenseTypeName(license.licenseType),
      value: license.id,
      seatsLine,
      expiryPrimary,
      expiryHint: "",
      expirySoon: false,
      selectionExpiryLine: expiryPrimary,
      props: { disabled: listDisabled }
    };
  }

  const end = new Date(license.expireAt);
  const msLeft = end.getTime() - now.getTime();

  if(msLeft <= 0) {
    const expiryPrimary = t("license_expired");
    return {
      title: getLicenseTypeName(license.licenseType),
      value: license.id,
      seatsLine,
      expiryPrimary,
      expiryHint,
      expirySoon: true,
      selectionExpiryLine: expiryPrimary,
      props: { disabled: listDisabled }
    };
  }

  if(msLeft < SOON_MS) {
    const days = Math.max(1, Math.ceil(msLeft / DAY_MS));
    const expiryPrimary = t("license_select_days_left", { days });
    return {
      title: getLicenseTypeName(license.licenseType),
      value: license.id,
      seatsLine,
      expiryPrimary,
      expiryHint,
      expirySoon: true,
      selectionExpiryLine: expiryPrimary,
      props: { disabled: listDisabled }
    };
  }

  const expiryPrimary = d(end, "short");
  return {
    title: getLicenseTypeName(license.licenseType),
    value: license.id,
    seatsLine,
    expiryPrimary,
    expiryHint,
    expirySoon: false,
    selectionExpiryLine: expiryPrimary,
    props: { disabled: listDisabled }
  };
}

function sortMostTimeLeftFirst(a: License, b: License): number {
  const ta = a.expireAt ? new Date(a.expireAt).getTime() : Number.MAX_SAFE_INTEGER;
  const tb = b.expireAt ? new Date(b.expireAt).getTime() : Number.MAX_SAFE_INTEGER;
  return tb - ta;
}

const selectItems = computed((): LicenseSelectRow[] => {
  const now = new Date();
  const m = model.value;

  const visibleLicenses = props.licenses.filter((license) => {
    if(!isExpired(license, now)) {
      return true;
    }
    return modelReferencesLicenseId(m, license.id);
  });

  const sorted = [...visibleLicenses].sort(sortMostTimeLeftFirst);
  const rows: LicenseSelectRow[] = [];

  if(props.actionHeadline) {
    rows.push({
      title: props.actionHeadline.title,
      value: props.actionHeadline.value,
      seatsLine: null,
      expiryPrimary: null,
      expiryHint: null,
      expirySoon: false,
      selectionExpiryLine: null,
      props: { disabled: false }
    });
  }

  if(props.withFreeTier) {
    rows.push({
      title: t("no_license"),
      value: null,
      seatsLine: null,
      expiryPrimary: null,
      expiryHint: null,
      expirySoon: false,
      selectionExpiryLine: null,
      props: { disabled: false }
    });
  }

  for(const license of sorted) {
    const expired = isExpired(license, now);
    const freeSeats = Math.max(0, license.maxUsers - (license._count?.users ?? 0));
    const listDisabled = freeSeats === 0 || expired;
    rows.push(licenseToRow(license, now, listDisabled));
  }

  return rows;
});
</script>

<style lang="scss" scoped>
.license-select-append {
  /* Tighter vertical rhythm next to multi-line titles. */
  margin-block: -0.25rem;
}

.license-select-row-check {
  flex-shrink: 0;
}

.license-select-row-check :deep(.v-selection-control) {
  min-height: 2.25rem;
}

.license-select-expiry {
  line-height: 1.25;
}

.license-select-selection {
  min-width: 0;
}

/* Keeps collapsed height aligned with rows that show an expiry line (caption row). */
.license-select-selection__sub {
  min-height: 1.25rem;
  line-height: 1.25rem;
}
</style>
